import React from 'react';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';

/**
 * Piezas visuales compartidas de los paneles.
 *
 * Todas entienden el tema oscuro y evitan la trampa de las tarjetas gigantes
 * con un número enorme y nada más: un dato sin referencia no dice nada. Cuando
 * hay historia, se muestra la tendencia al lado.
 */

export const Tarjeta = ({ children, className = '', ...resto }) => (
  <div
    className={`bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl ${className}`}
    {...resto}
  >
    {children}
  </div>
);

export const TarjetaTitulo = ({ titulo, detalle, accion }) => (
  <div className="flex flex-wrap items-start justify-between gap-3 px-5 pt-5 pb-4">
    <div className="min-w-0">
      <h2 className="font-heading text-base font-semibold">{titulo}</h2>
      {detalle && (
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">{detalle}</p>
      )}
    </div>
    {accion}
  </div>
);

export const TarjetaCuerpo = ({ children, className = '' }) => (
  <div className={`px-5 pb-5 ${className}`}>{children}</div>
);

/** Cifra con su etiqueta y, si corresponde, cómo viene respecto al período anterior. */
export const Metrica = ({ etiqueta, valor, icono: Icono, tendencia, pie, tono = 'neutro' }) => {
  const tonos = {
    neutro: 'text-slate-500 dark:text-slate-400',
    positivo: 'text-teal-600 dark:text-teal-400',
    alerta: 'text-amber-600 dark:text-amber-400',
    riesgo: 'text-red-600 dark:text-red-400',
  };

  const Flecha = tendencia > 0 ? TrendingUp : tendencia < 0 ? TrendingDown : Minus;
  const colorFlecha =
    tendencia > 0
      ? 'text-teal-600 dark:text-teal-400'
      : tendencia < 0
        ? 'text-red-600 dark:text-red-400'
        : 'text-slate-400';

  return (
    <Tarjeta className="p-5">
      <div className="flex items-start justify-between gap-2 mb-2">
        <span className="text-sm text-slate-500 dark:text-slate-400">{etiqueta}</span>
        {Icono && <Icono className={`w-4 h-4 flex-none ${tonos[tono]}`} />}
      </div>

      <div className="flex items-end gap-2">
        <span className="font-heading text-2xl font-semibold tabular-nums">{valor}</span>
        {tendencia !== undefined && tendencia !== null && (
          <span className={`flex items-center gap-0.5 text-xs mb-1 ${colorFlecha}`}>
            <Flecha className="w-3 h-3" />
            {Math.abs(tendencia)}%
          </span>
        )}
      </div>

      {pie && <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">{pie}</p>}
    </Tarjeta>
  );
};

/**
 * Gráfico de barras mínimo, dibujado con divs.
 * Sin librerías: son 20 barras, no vale la pena sumar 100 KB al paquete.
 */
export const Barras = ({ datos, etiquetaValor = (v) => v, alto = 'h-32' }) => {
  const max = Math.max(1, ...datos.map((d) => d.valor));

  if (datos.every((d) => d.valor === 0)) {
    return (
      <p className="text-sm text-slate-500 dark:text-slate-400 py-8 text-center">
        Sin movimiento en este período.
      </p>
    );
  }

  return (
    <div>
      <div className={`flex items-end gap-1 ${alto}`}>
        {datos.map((d, i) => (
          <div key={i} className="flex-1 flex flex-col justify-end group relative min-w-0">
            <div
              className={`rounded-t transition-colors ${
                d.valor > 0
                  ? 'bg-teal-500 group-hover:bg-teal-400'
                  : 'bg-slate-200 dark:bg-slate-800'
              }`}
              style={{ height: `${Math.max(3, (d.valor / max) * 100)}%` }}
            />
            <div className="absolute bottom-full mb-1.5 left-1/2 -translate-x-1/2 hidden group-hover:block whitespace-nowrap bg-slate-900 dark:bg-slate-700 text-white text-xs rounded-md px-2 py-1 z-10 pointer-events-none">
              {d.etiqueta}: {etiquetaValor(d.valor)}
            </div>
          </div>
        ))}
      </div>

      {datos.length > 1 && (
        <div className="flex justify-between text-xs text-slate-500 dark:text-slate-400 mt-2">
          <span>{datos[0].etiqueta}</span>
          <span>{datos[datos.length - 1].etiqueta}</span>
        </div>
      )}
    </div>
  );
};

/** Etiqueta de estado, con los colores consistentes en toda la aplicación. */
export const Estado = ({ children, tono = 'neutro' }) => {
  const tonos = {
    neutro: 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300',
    exito: 'bg-teal-50 dark:bg-teal-500/10 text-teal-700 dark:text-teal-300',
    alerta: 'bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-300',
    riesgo: 'bg-red-50 dark:bg-red-500/10 text-red-700 dark:text-red-300',
  };

  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${tonos[tono]}`}>
      {children}
    </span>
  );
};

/** Cuando no hay nada que mostrar, decir qué hacer en vez de dejar el vacío. */
export const Vacio = ({ icono: Icono, titulo, texto, accion }) => (
  <div className="text-center py-12 px-4">
    {Icono && (
      <div className="w-12 h-12 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center mx-auto mb-4">
        <Icono className="w-6 h-6 text-slate-500 dark:text-slate-400" />
      </div>
    )}
    <p className="font-medium mb-1">{titulo}</p>
    {texto && <p className="text-sm text-slate-500 dark:text-slate-400 max-w-sm mx-auto">{texto}</p>}
    {accion && <div className="mt-5">{accion}</div>}
  </div>
);

/** Bloque de carga: mejor que un "Cargando…" suelto en medio de la pantalla. */
export const Esqueleto = ({ filas = 3 }) => (
  <div className="space-y-3 animate-pulse" aria-hidden="true">
    {Array.from({ length: filas }, (_, i) => (
      <div key={i} className="h-16 rounded-lg bg-slate-100 dark:bg-slate-800" />
    ))}
  </div>
);
